import React from 'react';
import PropTypes from 'prop-types';

import {Box, CircularProgress, Dialog, DialogContent, DialogTitle} from 'views/components';
import backend from 'utils/backend';

import CommandResponseView from './CommandResponseView';

const CommandResponseModal = (props) => {
  const {
    clientRn,
    isRegenerating,
    onClose,
    onRegenerate,
    requester,
    response,
  } = props;

  const [clientData, setClientData] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;

    backend.client.getClient(clientRn).then((result) => {
      if (mounted) {
        setClientData(result.clientDetail);
      }
    });

    return () => {
      mounted = false;
    };
  }, [clientRn]);

  const communicationTarget = React.useMemo(() => {
    if (!clientData) {
      return null;
    }

    const getRelationshipContact = (relationship, type) => {
      return relationship?.contacts?.find((c) => c.type === type)?.detail || '';
    };

    const relationships = Object.values(clientData.relationships ?? {})
        .sort((a, b) => a.priority - b.priority);

    const defaultRelationship =
      relationships.find((r) => Number(r.priority) === 1) || relationships[0] || null;

    return {
      defaultRelationship,
      relationships,
      getRelationshipEmail: (relationship) => getRelationshipContact(relationship, 'Email'),
      getRelationshipSms: (relationship) => getRelationshipContact(relationship, 'Phone'),
      sendEmail: async (emailData, targetRelationshipRn, onSuccess, onError = () => undefined) => {
        const relationshipRn = targetRelationshipRn || defaultRelationship?.relationshipRn;

        if (!relationshipRn) {
          onError();
          return null;
        }

        try {
          const result = await backend.notification.sendEmail(relationshipRn, emailData);
          onSuccess?.(result);
          return result;
        } catch (err) {
          onError?.(err);
        }
      },
      sendSms: async (smsData, smsRequester, targetRelationshipRn, onSuccess, onError = () => undefined) => {
        const relationshipRn = targetRelationshipRn || defaultRelationship?.relationshipRn;

        if (!relationshipRn) {
          onError();
          return null;
        }

        try {
          await backend.notification.sendSms(relationshipRn, smsData.message, smsRequester?.email);
          onSuccess?.();
        } catch (err) {
          onError?.(err);
        }
      },
      updateCareplan: async (careplan) => {
        return backend.client.updateCareplan(clientRn, careplan);
      },
    };
  }, [clientData, clientRn]);

  return (
    <Dialog open onClose={onClose} maxWidth='md'>
      <DialogTitle onClose={onClose}>
        {' '}
      </DialogTitle>
      <DialogContent>
        {
          communicationTarget ?
            <CommandResponseView
              communicationTarget={communicationTarget}
              isRegenerating={isRegenerating}
              onApproveAndSendSuccess={onClose}
              onDiscard={onClose}
              onRegenerate={onRegenerate}
              requester={requester}
              response={response}
            /> :
            <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
              <CircularProgress />
            </Box>
        }
      </DialogContent>
    </Dialog>
  );
};

CommandResponseModal.propTypes = {
  clientRn: PropTypes.string.isRequired,
  isRegenerating: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onRegenerate: PropTypes.func,
  requester: PropTypes.object.isRequired,
  response: PropTypes.object.isRequired,
};

export default CommandResponseModal;
